import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import { useWidgetFrame } from '../frameContext';
import { labelSx, valueFontSize, valueSx } from './mobileText';

/**
 * KPI tile (design template 1). Sizes S and W. Headless: the frame draws the edit control row
 * in edit mode, so the icon row is hidden then. Root carries the testid and cursor so the
 * legacy dashboard E2E assertions keep working.
 */
export default function KpiTile({
  icon: Icon,
  value,
  label,
  trend,
  delta,
  sub,
  subTone,
  progress,
  action,
  centered = false,
  onClick,
  testId,
  iconColor = 'secondary.main',
  iconBg = 'rgba(49, 130, 206, 0.1)',
}) {
  const frame = useWidgetFrame();
  const edit = frame?.mode === 'edit';
  const mobile = Boolean(frame?.isMobile);
  const clickable = Boolean(onClick) && !edit && !frame?.inPreview;

  const hasProgress = progress != null;
  const iconSize = hasProgress ? 34 : 40;

  return (
    <Box
      data-testid={testId}
      onClick={clickable ? onClick : undefined}
      sx={{
        flex: 1,
        minHeight: 0,
        px: 2.25,
        py: hasProgress ? 1.75 : 2.25,
        display: 'flex',
        flexDirection: 'column',
        alignItems: centered ? 'center' : 'stretch',
        textAlign: centered ? 'center' : 'left',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        borderRadius: '12px',
        overflow: 'hidden',
        ...(clickable ? { '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,.08)' } } : {}),
      }}
    >
      {!edit ? (
        <Box sx={{ display: 'flex', justifyContent: centered ? 'center' : 'space-between', alignItems: 'flex-start', mb: hasProgress ? 0.75 : 1 }}>
          {Icon ? (
            <Box
              sx={{
                width: iconSize,
                height: iconSize,
                borderRadius: '10px',
                bgcolor: iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: iconColor,
                flexShrink: 0,
              }}
            >
              <Icon />
            </Box>
          ) : null}
          {trend?.text && !centered ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 13, fontWeight: 500, color: trend.up ? '#2f8c54' : '#c53030' }}>
              <TrendingUpIcon fontSize="small" sx={{ transform: trend.up ? 'none' : 'rotate(180deg)' }} />
              {trend.text}
            </Box>
          ) : delta && !centered ? (
            <Box sx={{ fontSize: 13, fontWeight: 500, color: '#2f8c54' }}>{delta}</Box>
          ) : null}
        </Box>
      ) : null}
      <Typography
        data-widget-value=""
        sx={{
          fontSize: valueFontSize(value, mobile, 28),
          lineHeight: 1.1,
          fontWeight: 700,
          letterSpacing: '-0.5px',
          color: 'text.primary',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          ...valueSx(mobile),
        }}
      >
        {value}
      </Typography>
      <Typography sx={{ fontSize: 13, lineHeight: 1.3, color: 'text.secondary', mt: 0.5, ...labelSx(mobile) }}>
        {label}
        {sub ? (
          <Box component="span" sx={{ color: subTone === 'red' ? '#c53030' : 'text.secondary', fontWeight: subTone === 'red' ? 600 : 400 }}>
            {' · '}
            {sub}
          </Box>
        ) : null}
      </Typography>
      {progress != null ? (
        <LinearProgress
          variant="determinate"
          value={Math.max(0, Math.min(100, progress))}
          sx={{ mt: 1, height: 6, borderRadius: 3, flexShrink: 0, bgcolor: '#edf2f7', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: progress >= 100 ? '#c53030' : '#3182ce' } }}
        />
      ) : null}
      {action ? (
        <Typography
          component="button"
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            action.onClick();
          }}
          sx={{ alignSelf: centered ? 'center' : 'flex-start', mt: 'auto', background: 'none', border: 0, p: 0, cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'secondary.main', fontFamily: 'inherit' }}
        >
          {action.label}
        </Typography>
      ) : null}
    </Box>
  );
}
